import { useCallback, useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router"
import { CalendarDaysIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import EnkIwpLogo from "../components/iwp-logo"
import { EnkApiError } from "../api/do-request"
import { classNames } from "../utils/styling-utils"
import DagCard, { formatDate } from "../components/planner/dag-card"
import {
  EnkIWPServerPlanToken,
  TEnkIWPAvailabilityDay,
  TEnkIWPAvailabilityResult,
  TEnkIWPPlanBonInfo,
  TEnkIWPPlanOverview,
  TEnkIWPTimeSlot,
} from "../api/api-iwp-server"

type PageState = "loading" | "overview" | "selectSlot" | "scheduling" | "done" | "error"

export default function EnkPlanTokenPage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const bonParam = searchParams.get("bon")

  const [pageState, setPageState] = useState<PageState>("loading")
  const [error, setError] = useState<string | null>(null)
  const [overview, setOverview] = useState<TEnkIWPPlanOverview | null>(null)

  // Inplan-flow state
  const [selectedBon, setSelectedBon] = useState<TEnkIWPPlanBonInfo | null>(null)
  const [availability, setAvailability] = useState<TEnkIWPAvailabilityResult | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<{ dag: TEnkIWPAvailabilityDay; slot: TEnkIWPTimeSlot } | null>(null)
  const [resultMessage, setResultMessage] = useState("")
  const [bevestigingVereist, setBevestigingVereist] = useState(false)

  // H-6: loadOverview gedefinieerd voor useEffect om stale closure te voorkomen
  const loadOverview = useCallback(async () => {
    if (!token) return
    setPageState("loading")
    try {
      const result = await EnkIWPServerPlanToken.getOverview(token)
      setOverview(result)
      setPageState("overview")
    } catch (err) {
      if (err instanceof EnkApiError) {
        if (err.httpStatusCode === 401 || err.httpStatusCode === 403 || err.httpStatusCode === 410) {
          setError("Deze link is verlopen of niet meer geldig. Neem contact op met uw installateur voor een nieuwe link.")
        } else {
          setError(err.description || "Er is een fout opgetreden bij het laden.")
        }
      } else {
        setError("Er is een fout opgetreden. Probeer het later opnieuw.")
      }
      setPageState("error")
    }
  }, [token])

  // Laad overzicht bij openen
  useEffect(() => {
    if (!token) {
      setError("Geen geldige link. Controleer de link uit de e-mail.")
      setPageState("error")
      return
    }
    void loadOverview()
  }, [token, loadOverview])

  // Haal beschikbaarheid op voor een bon
  const fetchAvailability = useCallback(async (bon: TEnkIWPPlanBonInfo) => {
    if (!token || !overview) return
    // M-7: Valideer csrfToken aanwezigheid
    if (!overview.csrfToken) {
      setError("Beveiligingstoken ontbreekt. Herlaad de pagina.")
      return
    }
    setSelectedBon(bon)
    setError(null) // M-6: Wis eerdere error
    setPageState("loading")
    setSelectedSlot(null)

    try {
      const result = await EnkIWPServerPlanToken.getAvailability(token, {
        opdrachtbonnr: bon.opdrachtbonnr,
        csrfToken: overview.csrfToken,
      })
      setAvailability(result)
      setPageState("selectSlot")
    } catch (err) {
      const msg = err instanceof EnkApiError ? err.description : "Fout bij het ophalen van beschikbaarheid."
      setError(msg)
      setPageState("overview")
    }
  }, [token, overview])

  // Auto-selecteer bon als ?bon= parameter meegegeven is (alleen eerste keer)
  const [bonAutoSelected, setBonAutoSelected] = useState(false)
  useEffect(() => {
    if (!bonParam || !overview || pageState !== "overview" || bonAutoSelected) return
    const bon = overview.bonnen?.find((b: TEnkIWPPlanBonInfo) => b.opdrachtbonnr === bonParam)
    if (bon) {
      setBonAutoSelected(true)
      void fetchAvailability(bon)
    }
  }, [bonParam, overview, pageState, bonAutoSelected, fetchAvailability])

  // Plan de afspraak in
  const handleSchedule = useCallback(async () => {
    if (!token || !overview || !selectedSlot || !selectedBon) return
    // M-7: Valideer csrfToken aanwezigheid
    if (!overview.csrfToken) {
      setError("Beveiligingstoken ontbreekt. Herlaad de pagina.")
      return
    }

    setError(null) // M-6: Wis eerdere error
    setPageState("scheduling")
    try {
      const result = await EnkIWPServerPlanToken.schedule(token, {
        opdrachtbonnr: selectedBon.opdrachtbonnr,
        datum: selectedSlot.dag.datum,
        tijdblokId: selectedSlot.slot.tijdblokId,
        csrfToken: overview.csrfToken,
      })

      if (result.success) {
        setResultMessage(result.melding ?? "Afspraak succesvol ingepland.")
        setBevestigingVereist(result.bevestigingVereist)
        setPageState("done")
      } else {
        setError(result.melding ?? "Er is een fout opgetreden bij het inplannen.")
        setPageState("selectSlot")
      }
    } catch (err) {
      const msg = err instanceof EnkApiError ? err.description : "Fout bij het inplannen."
      setError(msg)
      setPageState("selectSlot")
    }
  }, [token, overview, selectedSlot, selectedBon])

  // Terug naar overzicht
  const handleBackToOverview = () => {
    setSelectedBon(null)
    setAvailability(null)
    setSelectedSlot(null)
    setError(null)
    setPageState("overview")
  }

  // Na inplannen: herlaad overzicht
  const handleBackAfterDone = useCallback(async () => {
    setSelectedBon(null)
    setAvailability(null)
    setSelectedSlot(null)
    setError(null)
    setResultMessage("")
    try {
      await loadOverview()
    } catch {
      // loadOverview handelt errors zelf af via setError/setPageState
    }
  }, [loadOverview])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center">
          <EnkIwpLogo />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Error melding (inline, niet fatal) */}
        {error && pageState !== "error" && (
          <div className="text-red-600 text-sm font-medium py-3 px-4 bg-red-50 rounded-md mb-4 flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {(pageState === "loading" || pageState === "scheduling") && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent mb-4"></div>
            <p className="text-sm text-gray-500">
              {pageState === "scheduling" ? "Afspraak wordt ingepland..." : "Gegevens laden..."}
            </p>
          </div>
        )}

        {/* Fatale error (ongeldig/verlopen token) */}
        {pageState === "error" && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Link niet geldig</h2>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
        )}

        {/* Overzicht: alle bonnen van de relatie */}
        {pageState === "overview" && overview && (
          <div>
            <div className="mb-6">
              <h1 className="text-xl font-semibold text-gray-900">
                Afspraak plannen
              </h1>
              {overview.relatieNaam && (
                <p className="text-sm text-gray-500 mt-1">{overview.relatieNaam}</p>
              )}
            </div>

            {overview.bonnen.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
                <CalendarDaysIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Er zijn geen openstaande opdrachten om in te plannen.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {overview.bonnen.map((bon) => (
                  <BonCard
                    key={bon.opdrachtbonnr}
                    bon={bon}
                    onPlannen={() => void fetchAvailability(bon)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tijdblok selecteren */}
        {pageState === "selectSlot" && availability && selectedBon && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {availability.omschrijving ?? `Opdrachtbon ${availability.opdrachtbonnr}`}
                </h2>
                <p className="text-sm text-gray-500">Kies een beschikbaar tijdblok</p>
              </div>
              <button
                onClick={handleBackToOverview}
                className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Terug
              </button>
            </div>

            {availability.dagen.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <CalendarDaysIcon className="h-12 w-12 mb-3" />
                <p className="text-sm font-medium text-gray-500">Geen beschikbare dagen gevonden</p>
                <p className="text-xs text-gray-400 mt-1">Er zijn momenteel geen tijdblokken beschikbaar.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {availability.dagen.map((dag) => (
                  <DagCard
                    key={dag.datum}
                    dag={dag}
                    selectedSlot={selectedSlot}
                    onSelectSlot={(slot) => setSelectedSlot({ dag, slot })}
                  />
                ))}
              </div>
            )}

            {/* Inplannen knop */}
            {selectedSlot && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 py-4 mt-6 -mx-4 px-4">
                <div className="flex items-center justify-between max-w-3xl mx-auto">
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{selectedSlot.dag.dagNaam}</span>{" "}
                    {formatDate(selectedSlot.dag.datum)} &mdash;{" "}
                    <span className="font-medium">
                      {selectedSlot.slot.omschrijving ?? `${selectedSlot.slot.beginTijd} - ${selectedSlot.slot.eindTijd}`}
                    </span>
                  </div>
                  <button
                    onClick={() => void handleSchedule()}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
                  >
                    Inplannen
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bevestiging */}
        {pageState === "done" && (
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {bevestigingVereist ? "Aanvraag ingediend" : "Afspraak bevestigd"}
            </h2>
            <p className="text-sm text-gray-600 mb-6">{resultMessage}</p>
            {bevestigingVereist && (
              <p className="text-xs text-gray-400 mb-6">
                Uw afspraak moet nog bevestigd worden. U ontvangt een e-mail zodra dit is gebeurd.
              </p>
            )}
            <button
              onClick={() => void handleBackAfterDone()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
            >
              Terug naar overzicht
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** Status badge kleuren */
const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  pbsOpenstaand: { label: "Nog niet ingepland", bg: "bg-gray-100", text: "text-gray-600" },
  pbsAangevraagd: { label: "Wacht op bevestiging", bg: "bg-yellow-100", text: "text-yellow-700" },
  pbsBevestigd: { label: "Bevestigd", bg: "bg-green-100", text: "text-green-700" },
  pbsGeweigerd: { label: "Geweigerd", bg: "bg-red-100", text: "text-red-700" },
}

/** Kaart voor een bon in het overzicht */
function BonCard({ bon, onPlannen }: { bon: TEnkIWPPlanBonInfo; onPlannen: () => void }) {
  const config = statusConfig[bon.status] ?? statusConfig.pbsOpenstaand
  const kanInplannen = bon.status === "pbsOpenstaand"

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">{bon.opdrachtbonnr}</span>
            <span className={classNames("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", config.bg, config.text)}>
              {bon.statusTekst || config.label}
            </span>
          </div>
          <p className="text-sm text-gray-600 truncate">{bon.omschrijving}</p>
          {bon.datum && bon.tijdblok && (
            <p className="text-xs text-gray-400 mt-1">
              {formatDate(bon.datum)} &mdash; {bon.tijdblok}
            </p>
          )}
        </div>
        {kanInplannen && (
          <button
            onClick={onPlannen}
            className="flex-shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
          >
            Plan in
          </button>
        )}
      </div>
    </div>
  )
}

